import { fade } from "@material-ui/core/styles/colorManipulator";

// Theme palette is orange + red only, so stock tones are named here.
export const STOCK_TONE = {
  local: { color: "#1b5e20", background: "#e8f5e9" },
  eu: { color: "#8a6d1d", background: "#fff8e1" },
  ondemand: { color: "#0d47a1", background: "#e3f2fd" },
  shipping: "#1b5e20",
};

export default function tireCardStyles(theme) {
  return {
    // dir="rtl" is on the element. A direction value in this stylesheet would be flipped to ltr.
    card: {
      display: "flex",
      alignItems: "stretch",
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
      boxShadow: "none",
      overflow: "hidden",
      transition: "0.15s ease-in-out",
      "&:hover": {
        borderColor: theme.palette.grey[400],
        boxShadow: theme.shadows[1],
      },
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
      },
    },
    media: {
      display: "flex",
      alignItems: "stretch",
      flexShrink: 0,
      boxSizing: "border-box",
      width: 256,
      padding: theme.spacing(1.5),
      backgroundColor: theme.palette.grey[50],
      [theme.breakpoints.down("sm")]: {
        width: "auto",
        height: 140,
      },
    },
    imageFrame: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minWidth: 0,
      "& + &": {
        marginLeft: theme.spacing(1),
      },
    },
    image: {
      flex: 1,
      width: "100%",
      minHeight: 128,
      borderRadius: 8,
      backgroundColor: theme.palette.grey[100],
      backgroundSize: "contain",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      [theme.breakpoints.down("sm")]: {
        minHeight: 0,
      },
    },
    imageCaption: {
      color: theme.palette.text.secondary,
      lineHeight: 1.3,
      flexShrink: 0,
      display: "block",
      textAlign: "center",
      marginTop: theme.spacing(0.5),
    },
    details: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: theme.spacing(2),
    },
    heading: {
      display: "flex",
      alignItems: "center",
    },
    brandLogo: {
      display: "block",
      height: 36,
      width: "auto",
      maxWidth: 112,
      objectFit: "contain",
      flexShrink: 0,
    },
    brandFallback: {
      letterSpacing: "normal",
      textTransform: "none",
      lineHeight: 1.3,
      fontWeight: 700,
      color: theme.palette.text.secondary,
      flexShrink: 0,
    },
    headingText: {
      minWidth: 0,
      marginLeft: theme.spacing(1.5),
    },
    title: {
      fontWeight: 700,
      lineHeight: 1.3,
    },
    titleLink: {
      color: "inherit",
      textDecoration: "none",
      "&:hover": {
        color: theme.palette.primary.main,
      },
    },
    meta: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
    },
    metaItem: {
      marginTop: theme.spacing(0.75),
      marginRight: theme.spacing(1),
    },
    kindChip: {
      fontWeight: 600,
      lineHeight: 1.4,
      display: "inline-flex",
      alignItems: "center",
      height: 24,
      padding: "0 8px",
      borderRadius: 6,
    },
    kindChipSet: {
      color: theme.palette.primary.dark,
      backgroundColor: fade(theme.palette.primary.main, 0.12),
    },
    kindChipPair: {
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.grey[100],
    },
    offerChip: {
      fontWeight: 600,
      lineHeight: 1.4,
      color: theme.palette.secondary.dark,
      display: "inline-flex",
      alignItems: "center",
      height: 24,
      padding: "0 8px",
      borderRadius: 6,
      backgroundColor: fade(theme.palette.secondary.main, 0.12),
    },
    score: {
      display: "inline-flex",
      alignItems: "center",
      whiteSpace: "nowrap",
      color: theme.palette.text.secondary,
    },
    rowScore: {
      marginRight: theme.spacing(1),
    },
    scoreIcon: {
      marginLeft: 2,
    },
    axles: {
      marginTop: theme.spacing(1.5),
    },
    axleRow: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      minHeight: 40,
      marginTop: theme.spacing(0.5),
      marginLeft: -8,
      marginRight: -8,
      padding: "4px 8px",
      borderRadius: 8,
      textDecoration: "none",
      color: "inherit",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    axleLabel: {
      fontWeight: 700,
      lineHeight: 1.4,
      color: theme.palette.text.secondary,
      flexShrink: 0,
      minWidth: 48,
      marginRight: theme.spacing(1),
    },
    axleSize: {
      minWidth: 0,
      marginRight: theme.spacing(1),
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      flexShrink: 0,
      borderRadius: 999,
      padding: "2px 8px",
      marginRight: theme.spacing(1),
      whiteSpace: "nowrap",
    },
    badgeDot: {
      width: 6,
      height: 6,
      borderRadius: "50%",
      backgroundColor: "currentColor",
      marginRight: 6,
    },
    badgeLocal: {
      color: STOCK_TONE.local.color,
      backgroundColor: STOCK_TONE.local.background,
    },
    badgeEu: {
      color: STOCK_TONE.eu.color,
      backgroundColor: STOCK_TONE.eu.background,
    },
    badgeOndemand: {
      color: STOCK_TONE.ondemand.color,
      backgroundColor: STOCK_TONE.ondemand.background,
    },
    badgeOut: {
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.grey[200],
    },
    unitPrice: {
      fontWeight: 700,
      lineHeight: 1.4,
      marginLeft: "auto",
      whiteSpace: "nowrap",
    },
    cta: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "stretch",
      flexShrink: 0,
      boxSizing: "border-box",
      width: 220,
      padding: theme.spacing(2),
      textAlign: "center",
      borderLeft: `1px solid ${theme.palette.divider}`,
      [theme.breakpoints.down("sm")]: {
        width: "auto",
        borderLeft: "none",
        borderTop: `1px solid ${theme.palette.divider}`,
      },
    },
    priceLabel: {
      letterSpacing: "normal",
      textTransform: "none",
      lineHeight: 1.4,
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
    totalPrice: {
      fontWeight: 700,
      lineHeight: 1.2,
      marginTop: theme.spacing(0.5),
    },
    shipping: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginTop: theme.spacing(1),
      color: STOCK_TONE.shipping,
    },
    shippingIcon: {
      marginRight: theme.spacing(0.5),
    },
    shippingText: {
      fontWeight: 600,
      lineHeight: 1.4,
      color: STOCK_TONE.shipping,
    },
    orderButton: {
      textTransform: "none",
      fontWeight: 700,
      minHeight: 44,
      borderRadius: 8,
      marginTop: theme.spacing(2),
    },
    detailsButton: {
      textTransform: "none",
      fontWeight: 600,
      marginTop: theme.spacing(0.5),
    },
  };
}
